import { handleManualFilterChange } from '@/redux/store/slices/grid/gridSlice'
import { isToolEnabledSelector, selectedToolSelector } from '@/redux/store/slices/map/mapSlice'
import { useAppDispatch, useAppSelector } from '@redux/hooks/useAppState'
import { useCallback, useState } from 'react'

import type { AggregatedDemographics } from 'honeygrid-types'
import type { MapMouseEvent, PointLike } from 'mapbox-gl'
import type { MapRef } from 'react-map-gl/mapbox'

export const useOnCellClicked = (
	mapRef: React.RefObject<MapRef | null>,
	aggregatedDemographics?: AggregatedDemographics
) => {
	const cellMap = aggregatedDemographics?.cells
	const [clickedCellId, setClickedCellId] = useState<string | null>(null)
	const isMapToolEnabled = useAppSelector(isToolEnabledSelector)
	const selectedTool = useAppSelector(selectedToolSelector)
	const dispatch = useAppDispatch()
	const handleCellClicked = useCallback(
		(e: MapMouseEvent | null) => {
			if (e === null) {
				setClickedCellId(null)
				return
			}
			const { x, y } = e.point
			const bbox: [PointLike, PointLike] = [
				[x - 1, y - 1],
				[x + 1, y + 1],
			]
			const features = mapRef?.current?.queryRenderedFeatures(bbox) ?? []
			const clickedCellId = features.find((feature) =>
				['honeygrid-fill', 'inclusion-fill', 'exclusion-fill'].includes(feature.layer?.id ?? '')
			)?.properties?.id

			// No cell found for click
			if (!clickedCellId) return

			// If map tool is enabled, dispatch the action to update the cell
			if (isMapToolEnabled) {
				dispatch(
					handleManualFilterChange({
						cellId: clickedCellId,
						selectedTool,
					})
				)
			} else {
				// Otherwise, set the clicked cell id for the tooltip
				setClickedCellId((current) => {
					if (current) return null

					return clickedCellId
				})
			}
		},
		[dispatch, isMapToolEnabled, mapRef, selectedTool]
	)
	const clickedCell = cellMap && clickedCellId ? cellMap[clickedCellId] : null
	return [clickedCell, handleCellClicked] as const
}
